import { Router, NavModel, RouteConfig } from 'aurelia-router';
import { I18N } from 'aurelia-i18n';
import { mock, instance, verify, when, anyNumber, anything, resetCalls } from 'ts-mockito';
import { PaymentHistoryPage } from './payment-history';
import { PayrazrService } from '../../services/payrazr';
import { State } from '../../services/global-state';
import { Business } from '../../models/business';
import { Customer,Account } from '../../models/customer';
import { EventAggregator } from 'aurelia-event-aggregator';
import { PaymentAccountDisplayName } from '../../models/payment-account-display-name';

describe('NewPortalWeb', () => {
	describe('pages', () => {
		describe('payment-history', () => {
			let component: PaymentHistoryPage;
			const mockRouter: Router = mock(Router);
			const mockPayrazrService: PayrazrService = mock(PayrazrService);
			let stubState: State;
			const mockEventAggregator: EventAggregator = mock(EventAggregator);
			const mockBusiness: Business = mock(Business);
			const mockCustomer: Customer = mock(Customer);
			const mockI18N: I18N = mock(I18N);

			// Mock I18N Dependencies
			beforeEach(() => {
				const i18next = {
					t: (a, b) => a
				};
				when(mockI18N.i18next).thenReturn(i18next);
				spyOn(i18next, 't').and.callFake((key, defaultText) => {
					if (key === 'unavailable') {
						return 'unavailable-localized';
					} else {
						return 'localized';
					}
				});
			});

			// Initialize Component
			beforeEach(() => {
				stubState = new State(
					instance(mockI18N),
					instance(mockEventAggregator)
				);

				stubState.customer = instance(mockCustomer);
				stubState.business = instance(mockBusiness);

				component = new PaymentHistoryPage(
					instance(mockPayrazrService),
					stubState,
					instance(mockI18N),
					instance(mockRouter)
				);

			});

			it('should initialize component', () => {
				expect(component).toBeTruthy();
				expect(component.isLoading).toBeTruthy();
				expect(component.sortBy).toEqual('paymentDate');
				expect(component.sortDirection).toEqual('descending');
			});

			describe('sort()', () => {
				it('should change sort direction', () => {
					component.sortDirection = 'ascending';
					component.sort();
					expect(component.sortDirection).toEqual('descending');
					component.sort();
					expect(component.sortDirection).toEqual('ascending');
				});
			});

			describe('getAccountDisplayName()', () => {
				it('should return unavailable localized with no display names', () => {
					component.paymentAccountDisplayNames = null;
					const name = component.getAccountDisplayName('');
					expect(name).toEqual('unavailable-localized');
				});
				it('should return unavailable localized, when name not found', () => {
					component.paymentAccountDisplayNames = [
						{
							referenceId: 'testId',
							displayName: 'testName'
						}
					];
					const name = component.getAccountDisplayName('missing');
					expect(name).toEqual('unavailable-localized');
				});
				it('should return displayName when found', () => {
					component.paymentAccountDisplayNames = [
						{
							referenceId: 'testId',
							displayName: 'testName'
						}
					];
					const name = component.getAccountDisplayName('testId');
					expect(name).toEqual('testName');
				});
			});

			describe('goToReceipt()', () => {
				it('should set payments and navigate to receipt', () => {
					const routes = [
						{
							name: 'paymentHistoryReceipt',
							href: 'testHref',
							route: 'testRoute',
							payment: undefined
						},
						{
							name: 'test2',
							href: 'testHref',
							route: 'testRoute'
						}
					];
					const payment = {
						paymentDate: 'test',
						accountName: 'test',
						contactName: 'test',
						paymentAmount: 1000,
						result: 'test',
						PNRef: 'test',
						transactionType: 'test',
						message: 'test',
						startDate: 'test',
						bucketKey: 1
					};
					when(mockRouter.routes).thenReturn(routes);
					component.goToReceipt(payment);
					expect(routes[0].payment).toEqual(payment);
					verify(mockRouter.navigate(anything())).called();
				});
			});

			describe('getPaymentHistory()', async () => {
				const history = [
					{
						accountNumber: '1',
						contactName: 'test',
						paymentAmount: 1000,
						paymentDate: '01/01/2020',
						paymentResult: 'Approved',
						pnRef: 'test',
						transactionType: 'test',
						message: 'test',
						startDate: ''
					},
					{
						accountNumber: '2',
						contactName: 'test',
						paymentAmount: 2000,
						paymentDate: '01/02/2020',
						paymentResult: 'Approved',
						pnRef: 'test',
						transactionType: 'test',
						message: 'test',
						startDate: ''

					}
				];
				const displayNames: PaymentAccountDisplayName[] = [
					{
						referenceId: '1',
						displayName: 'test'
					},
					{
						referenceId: '2',
						displayName: 'test'
					}
				];
				it('should set payments, last payment date, and display names', async () => {
					component.payments = [];
					when(mockPayrazrService.getPaymentHistory(anything())).thenResolve(history);
					when(mockCustomer.accounts).thenReturn([
						new Account({ name: '1' }),
						new Account({ name: '2' })
					]);
					when(mockBusiness.fetchPortfolioSettings(anything())).thenReturn({
						merchantSettings: {
							thirdPartyIntegrationSettings: {
								platform: 'testName'
							}
						}
					});
					when(mockPayrazrService.referenceIdWrapper(anything(), anything(), anything())).thenReturn('test');

					await component.getPaymentHistory(anything());
					expect(component.payments.length).toEqual(2);
					expect(component.paymentAccountDisplayNames.length).toEqual(2);
					expect(component.isLoading).toBeFalsy();
				});

				it('should handle errors', async () => {
					when(mockPayrazrService.getPaymentHistory(anything()))
						.thenReject();
					when(mockCustomer.accounts).thenReturn([
						new Account({ name: '1' }),
						new Account({ name: '2' })
					]);
					await component.getPaymentHistory(anything());
					expect(component.error).toEqual('localized');
					expect(component.isLoading).toBeFalsy();
				});
			});
		});
	});
});
